import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/hen-ui/utils/index';

interface TableContextValue {
  stackOnMobile: boolean;
  headers: string[];
  setHeaders: React.Dispatch<React.SetStateAction<string[]>>;
}

const TableContext = React.createContext<TableContextValue>({
  stackOnMobile: false,
  headers: [],
  setHeaders: () => {},
});

function extractText(node: React.ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (React.isValidElement(node))
    return extractText((node.props as { children?: React.ReactNode }).children);
  if (Array.isArray(node)) return node.map(extractText).join('');
  return '';
}

export interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  noWrapper?: boolean;
  asChild?: boolean;
  stackOnMobile?: boolean;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  (
    {
      className,
      noWrapper = false,
      asChild = false,
      stackOnMobile = false,
      ...props
    },
    ref,
  ) => {
    const [headers, setHeaders] = React.useState<string[]>([]);
    const Comp = asChild ? Slot : 'table';
    const table = (
      <Comp
        ref={ref}
        className={cn(
          'w-full caption-top font-body text-b5',
          stackOnMobile &&
            '[&_colgroup]:hidden [&_colgroup]:md:table-column-group',
          className,
        )}
        {...props}
      />
    );
    const wrapped = noWrapper ? (
      table
    ) : (
      <div className='relative w-full overflow-auto'>{table}</div>
    );
    return (
      <TableContext.Provider value={{ stackOnMobile, headers, setHeaders }}>
        {wrapped}
      </TableContext.Provider>
    );
  },
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, children, ...props }, ref) => {
  const { stackOnMobile, setHeaders } = React.useContext(TableContext);

  // Runs after hydration only — initial render (SSR + client) produces headers=[]
  // so no label spans exist during hydration, avoiding any mismatch.
  // Guard against calling setHeaders when labels haven't changed to avoid
  // unnecessary re-renders caused by children always having new object references.
  //
  // Uses the last header row exclusively so that grouped/multi-row headers don't
  // bleed earlier row labels into the column index space. Each <th> is expanded
  // by its colSpan so that headers[gridColumn] maps correctly for every body
  // cell — matching the colIndex assigned by TableRow.
  React.useEffect(() => {
    if (!stackOnMobile) return;
    const rows = React.Children.toArray(children).filter(React.isValidElement);
    const lastRow = rows[rows.length - 1] as
      | React.ReactElement<{ children?: React.ReactNode }>
      | undefined;
    if (!lastRow) return;
    const labels: string[] = [];
    React.Children.forEach(lastRow.props.children, (headChild) => {
      if (!React.isValidElement(headChild)) return;
      const text = extractText(
        (headChild.props as { children?: React.ReactNode }).children,
      );
      const span = (headChild.props as { colSpan?: number }).colSpan ?? 1;
      for (let i = 0; i < span; i++) labels.push(text);
    });
    setHeaders((prev) =>
      prev.length === labels.length && prev.every((h, i) => h === labels[i])
        ? prev
        : labels,
    );
  }, [stackOnMobile, children, setHeaders]);

  return (
    <thead
      ref={ref}
      className={cn(
        '[&_tr]:border-b',
        stackOnMobile && 'hidden md:table-header-group',
        className,
      )}
      {...props}
    >
      {children}
    </thead>
  );
});
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn(className)} {...props} />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => {
  const { stackOnMobile } = React.useContext(TableContext);
  return (
    <tfoot
      ref={ref}
      className={cn(
        'border-t border-neutral-200 bg-neutral-50 font-body font-bold text-b5 text-neutral-900 [&>tr]:last:border-b-0',
        stackOnMobile && 'block md:table-footer-group',
        className,
      )}
      {...props}
    />
  );
});
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, children, ...props }, ref) => {
  const { stackOnMobile } = React.useContext(TableContext);

  const indexedChildren = React.useMemo(() => {
    if (!stackOnMobile) return children;
    let index = 0;
    return React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        const colIndex = index;
        const colSpan = (child.props as { colSpan?: number }).colSpan ?? 1;
        index += colSpan;
        return React.cloneElement(
          child as React.ReactElement<{ _columnIndex?: number }>,
          { _columnIndex: colIndex },
        );
      }
      return child;
    });
  }, [stackOnMobile, children]);

  return (
    <tr
      ref={ref}
      className={cn(
        'border-b border-neutral-200 data-[state=selected]:bg-neutral-50',
        stackOnMobile && 'block md:table-row',
        className,
      )}
      {...props}
    >
      {indexedChildren}
    </tr>
  );
});
TableRow.displayName = 'TableRow';

interface TableHeadInternalProps
  extends React.ThHTMLAttributes<HTMLTableCellElement> {
  _columnIndex?: number;
}

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  TableHeadInternalProps
>(({ className, _columnIndex: _discarded, ...props }, ref) => {
  const { stackOnMobile } = React.useContext(TableContext);
  return (
    <th
      ref={ref}
      className={cn(
        'p-sm md:p-xs text-left align-middle font-body font-bold text-l1 text-neutral-500 [&:has([role=checkbox])]:pr-0',
        stackOnMobile && 'block md:table-cell',
        className,
      )}
      {...props}
    />
  );
});
TableHead.displayName = 'TableHead';

interface TableCellInternalProps
  extends React.TdHTMLAttributes<HTMLTableCellElement> {
  _columnIndex?: number;
}

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  TableCellInternalProps
>(({ className, children, _columnIndex, ...props }, ref) => {
  const { stackOnMobile, headers } = React.useContext(TableContext);
  // headers starts as [] on both server and client — label is undefined during
  // SSR and the initial client render, so the span is absent on both sides
  // (no hydration mismatch). Labels appear only after useEffect in TableHeader fires.
  const label = _columnIndex !== undefined ? headers[_columnIndex] : undefined;

  return (
    <td
      ref={ref}
      className={cn(
        'p-sm md:p-xs align-middle font-body text-b5 text-neutral-900 [&_a]:text-b5 [&_li]:text-b5 [&_li]:leading-b5 [&_li_p]:text-b5 [&_li_p]:leading-b5 [&:has([role=checkbox])]:pr-0',
        stackOnMobile && 'block md:table-cell',
        className,
      )}
      {...props}
    >
      {stackOnMobile && label && (
        <span className='mb-xxs block font-body font-bold text-l1 text-neutral-700 md:hidden'>
          {label}
        </span>
      )}
      {children}
    </td>
  );
});
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mb-lg text-left font-body text-b2 text-subtext', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
